import React, { useState, useEffect, useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { Box, Chip, Alert } from "@mui/material";
import {
  getVehicleSubscription,
  getVehicleSubscriptionAudit,
  updateSubscriptionEndDate,
} from "../../services/vehicle";
import { DataGrid, useGridApiRef } from "@mui/x-data-grid";
import Constants, { MODULES, MODULE_TEXT } from "../../services/Constants";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { DateAndTimeGridCell } from "../common/DateAndTimeGridCell";
import StatusActiveInactive from "../common/StatusActiveInactive";
import useModule from "../../hooks/useModule";
import { detailsTabsBoxStyle } from "../../services/Utils";
import { EditActionButton } from "../common/GridActionButton";
import { useToast } from "../toast";
import Check from "../../auth/ability";
import { CAN, SUBJECTS } from "../../auth/permissions";
import EditSubscriptionModal from "./modal/EditSubscriptionModal";

const displayServiceName = (name) => {
  if (!!name) {
    const parts = name
      .split("_")
      .map((i) => i?.charAt(0).toUpperCase() + i?.slice(1));
    return parts.join(" ");
  }
};

export default function VehicleSubscriptionList({ vehicleData, isLoading }) {
  const { validate } = useModule();
  const [value, setValue] = useState(0);
  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  if (!validate(MODULES.SUBSCRIPTION)) {
    return (
      <Box {...detailsTabsBoxStyle}>
        <Alert variant="outlined" severity="warning">
          {MODULE_TEXT.SUBSCRIPTION} module not enabled for{" "}
          {localStorage.selectedCustomer} customer
        </Alert>
      </Box>
    );
  }

  return (
    <Box {...detailsTabsBoxStyle}>
      <Tabs value={value} onChange={handleChange}>
        <Tab label="Subscriptions" />
        <Tab label="Subscription Audit" />
      </Tabs>
      {value === 1 ? (
        <SubscriptionsAuditList vehicleData={vehicleData} />
      ) : (
        <SubscriptionList vehicleData={vehicleData} />
      )}
    </Box>
  );
}

const SubscriptionList = ({ vehicleData }) => {
  const [pageSize, setPageSize] = useState(Constants.PAGE_SIZE);
  const [subscriptionData, setSubscriptionData] = useState([]);
  const apiRef = useGridApiRef();
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const processSubscriptionData = (subData) => {
    if (subData && Object.keys(subData).length) {
      const { features } = subData;
      if (features && features?.length) {
        return features
          .map((feature) => {
            const { services = [], name = "" } = feature;
            const list = services.map((i) => ({
              ...i,
              featureName: name,
            }));
            return list;
          })
          ?.flat();
      } else {
        return [];
      }
    } else {
      return [];
    }
  };

  const { data } = useQuery(
    ["vehicleSubscriptions", vehicleData?.device?.vehicleKey],
    getVehicleSubscription,
    {
      onSuccess: (res) => {
        console.log("subscription response is ", res);
        const result = processSubscriptionData(res);
        setSubscriptionData(result);
      },
      refetchOnWindowFocus: false,
      enabled: !!vehicleData?.device?.vehicleKey,
    }
  );

  const getColumnsData = (item) => {
    return [
      {
        field: "featureName",
        headerName: "Feature name",
        minWidth: 150,
        renderCell: (d) => renderServiceName(d.value),
      },
      {
        field: "name",
        headerName: "Service name",
        minWidth: 250,
        renderCell: (d) => d.value,
      } /*,
      {
        field: "createdAt",
        headerName: "Created On",
        renderCell: (d) => moment(d.value).format("L, h:mm:ss a")          
      }*/,
      {
        field: "subscriptionId",
        headerName: "Subscription ID",
        minWidth: 250,
      },
      {
        field: "subscriptionStartDate",
        headerName: "Subscription Start date",
        minWidth: 250,
        renderCell: (d) => <DateAndTimeGridCell value={d.value} />,
      },
      {
        field: "subscriptionEndDate",
        headerName: "Subscription End date",
        renderCell: (d) => (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <DateAndTimeGridCell value={d.value} />
            {canShowEditButton() && (
              <Check I={CAN.CREATE} a={SUBJECTS.IMAGE}>
                <EditActionButton
                  onClick={() => handleEditSubscription(d.row)}
                  iconOnly={true}
                  tooltip="Edit subscription end date"
                />
              </Check>
            )}
          </div>
        ),
      },
      {
        field: "transactionId",
        headerName: "Transaction ID",
      },
      {
        field: "pos",
        headerName: "POS",
      },
    ];
  };

  const columns = getColumnsData();

  useEffect(() => {
    if (!apiRef.current || !subscriptionData?.length) {
      return;
    }

    apiRef.current.autosizeColumns?.({
      columns: columns.map((col) => col.field),
      includeHeaders: true,
      includeOutliers: true,
    });
  }, [apiRef, columns, subscriptionData]);

  const renderServiceName = (i) => {
    return displayServiceName(i);
  };

  const { mutate: updateSubscriptionMutation, isLoading } = useMutation(
    updateSubscriptionEndDate,
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          "vehicleSubscriptions",
          vehicleData?.device?.vehicleKey,
        ]);
        addToast({
          type: "success",
          message: "Subscription end date updated successfully.",
          autoClose: 3000,
        });
        handleModalClose();
      },
      onError: (error) => {
        addToast({
          type: "error",
          message: error?.response?.data?.message || "Update failed!",
          autoClose: 3000,
        });
      },
    }
  );

  const handleEditSubscription = (rowData) => {
    setSelectedSubscription(rowData);
    setEditModalOpen(true);
  };

  const handleModalClose = () => {
    setEditModalOpen(false);
    setSelectedSubscription(null);
  };

  const handleSaveSubscription = async (updatedSubscription) => {
    updateSubscriptionMutation({
      subscriptionId: updatedSubscription.subscriptionId,
      payload: {
        subscriptionEndDate: updatedSubscription.subscriptionEndDate,
      },
    });
  };
  const canShowEditButton = () => {
    return (
      process.env.REACT_APP_STAGE !== "prod" &&
      (localStorage.selectedCustomer === "HD" || localStorage.selectedCustomer === "SKODA")
    );
  };


  return (
    <>
      {/* Your Data Grid */}
      <DataGrid
        apiRef={apiRef}
        autosizeOnMount
        pageSize={pageSize}
        onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
        rowsPerPageOptions={Constants.ROWS_PER_PAGE_OPT}
        pagination
        rows={subscriptionData || []}
        columns={columns}
        loading={isLoading}
        rowSpacingType="padding"
        sx={{
          minHeight: 700,
          border: "none",
        }}
        getRowId={(row) => row?.id + row?.subscriptionId}
      />
      <EditSubscriptionModal
        open={editModalOpen}
        onClose={handleModalClose}
        subscription={selectedSubscription}
        onSave={handleSaveSubscription}
      />
    </>
  );
};

const SubscriptionsAuditList = ({ vehicleData }) => {
  const [pageSize, setPageSize] = useState(Constants.PAGE_SIZE);
  const apiRef = useGridApiRef();
  const getAuditColumnsData = (item) => {
    return [
      {
        field: "createdAt",
        headerName: "Created On",
        width: 200,
        renderCell: (d) => <DateAndTimeGridCell value={d.value} />,
      },
      {
        field: "serviceName",
        headerName: "Service name",
        width: 150,
        renderCell: (d) => d.value,
      },
      {
        field: "subscriptionId",
        headerName: "Subscription ID",
        width: 350,
      },
      {
        field: "active",
        headerName: "Status",
        width: 150,
        renderCell: ({ row }) => <StatusActiveInactive active={row?.active} />,
      },
      {
        field: "subscriptionStartDate",
        headerName: "Subscription Start date",
        width: 200,
        renderCell: (d) => <DateAndTimeGridCell value={d.value} />,
      },
      {
        field: "subscriptionEndDate",
        headerName: "Subscription End date",
        width: 200,
        renderCell: (d) => <DateAndTimeGridCell value={d.value} />,
      },
      {
        field: "transaction",
        headerName: "Transaction",
        width: 150,
      },
      {
        field: "pos",
        headerName: "POS",
        width: 150,
      },
    ];
  };

  const auditColumns = getAuditColumnsData();

  const { data: auditData } = useQuery(
    ["vehicleSubscriptionsAudit", vehicleData?.device?.vehicleKey],
    getVehicleSubscriptionAudit,
    {
      refetchOnWindowFocus: false,
      enabled: !!vehicleData?.device?.vehicleKey,
    }
  );

  useEffect(() => {
    if (!apiRef.current || !auditData?.subscription?.length) {
      return;
    }

    apiRef.current.autosizeColumns?.({
      columns: auditColumns.map((col) => col.field),
      includeHeaders: true,
      includeOutliers: true,
      expandedRowCount: auditData?.subscription?.length,
    });
  }, [apiRef, auditColumns, auditData]);

  return (
    <DataGrid
      apiRef={apiRef}
      autosizeOnMount
      initialState={{
        sorting: {
          sortModel: [{ field: "createdAt", sort: "desc" }],
        },
      }}
      pageSize={pageSize}
      onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
      rowsPerPageOptions={Constants.ROWS_PER_PAGE_OPT}
      pagination
      rows={auditData?.subscriptionAudit || []}
      columns={auditColumns}
      rowSpacingType="padding"
      sx={{
        minHeight: 700,
        border: "none",
      }}
      getRowId={(row) => row.subscriptionAuditId}
    />
  );
};
